import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { mockLicenses } from '../../../lib/mock-data';
import { SkeletonCard, SkeletonTable } from '../../../components/common/SkeletonLoader';

/**
 * Creators Licenses - Manage product licenses and activations
 */
export const Route = createFileRoute('/_authenticated/creators/licenses')({
  component: CreatorsLicenses,
});

function CreatorsLicenses() {
  // Show first 4 licenses as sample data for creator view
  const [licenses, setLicenses] = useState<typeof mockLicenses>([]);
  const [loading, setLoading] = useState(true);
  const { theme } = useTheme();

  useEffect(() => {
    // Simulate API call
    const timer = setTimeout(() => {
      setLicenses(mockLicenses.slice(0, 4));
      setLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  const colors = {
    dark: {
      text: '#ffffff',
      textMuted: '#9ca3af',
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(102, 126, 234, 0.2)',
      dashedBorder: 'rgba(102, 126, 234, 0.3)',
      tableBg: 'rgba(15, 15, 20, 0.4)',
      tableHeaderBg: 'rgba(26, 26, 46, 0.6)'
    },
    light: {
      text: '#111827',
      textMuted: '#6b7280',
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      dashedBorder: '#e5e7eb',
      tableBg: '#ffffff',
      tableHeaderBg: '#f9fafb'
    }
  };

  const c = colors[theme];

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            color: c.text,
            marginBottom: '0.5rem'
          }}>
            License Management
          </h1>
          <p style={{ color: c.textMuted, fontSize: '1rem' }}>
            Monitor active licenses and manage customer activations
          </p>
        </div>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          {loading ? (
            <>
              <SkeletonCard theme={theme} />
              <SkeletonCard theme={theme} />
              <SkeletonCard theme={theme} />
            </>
          ) : (
            <>
              <div style={{
                background: c.cardBg,
                border: `1px solid ${c.cardBorder}`,
                borderRadius: '0.5rem',
                padding: '1.5rem',
                backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
              }}>
                <p style={{ fontSize: '0.875rem', color: c.textMuted, marginBottom: '0.5rem' }}>
                  Total Licenses
                </p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: c.text }}>{licenses.length}</p>
              </div>
              <div style={{
                background: c.cardBg,
                border: `1px solid ${c.cardBorder}`,
                borderRadius: '0.5rem',
                padding: '1.5rem',
                backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
              }}>
                <p style={{ fontSize: '0.875rem', color: c.textMuted, marginBottom: '0.5rem' }}>
                  Active Licenses
                </p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#059669' }}>{licenses.filter(l => l.status === 'active').length}</p>
              </div>
              <div style={{
                background: c.cardBg,
                border: `1px solid ${c.cardBorder}`,
                borderRadius: '0.5rem',
                padding: '1.5rem',
                backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
              }}>
                <p style={{ fontSize: '0.875rem', color: c.textMuted, marginBottom: '0.5rem' }}>
                  Suspended
                </p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#dc2626' }}>{licenses.filter(l => l.status === 'suspended').length}</p>
              </div>
            </>
          )}
        </div>

        {/* Licenses Table */}
        {loading ? (
          <SkeletonTable rows={4} columns={6} theme={theme} />
        ) : licenses.length === 0 ? (
          <div style={{
            background: c.cardBg,
            border: `2px dashed ${c.dashedBorder}`,
            borderRadius: '0.5rem',
            padding: '3rem',
            textAlign: 'center',
            backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
          }}>
            <p style={{
              fontSize: '1.125rem',
              color: c.textMuted,
              marginBottom: '1rem'
            }}>
              No licenses issued yet
            </p>
            <p style={{
              fontSize: '0.875rem',
              color: c.textMuted,
              opacity: 0.7
            }}>
              Licenses will appear here when customers purchase your products
            </p>
          </div>
        ) : (
          <div style={{
            background: c.tableBg,
            border: `1px solid ${c.cardBorder}`,
            borderRadius: '0.5rem',
            overflow: 'hidden',
            backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: c.tableHeaderBg }}>
                <tr>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.textMuted, textTransform: 'uppercase' }}>License Key</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.textMuted, textTransform: 'uppercase' }}>Product</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.textMuted, textTransform: 'uppercase' }}>Customer</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.textMuted, textTransform: 'uppercase' }}>Activations</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.textMuted, textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.textMuted, textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {licenses.map((license) => (
                  <tr key={license.key} style={{ borderTop: `1px solid ${c.cardBorder}` }}>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', fontFamily: "'JetBrains Mono', monospace", color: c.text, letterSpacing: '-0.02em' }}>
                      {license.key}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: c.text }}>{license.product}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: c.text }}>{license.customer}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: c.textMuted }}>
                      {license.activations}/{license.maxActivations}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: license.status === 'active' ? '#d1fae5' : '#fee2e2',
                        color: license.status === 'active' ? '#065f46' : '#991b1b'
                      }}>
                        {license.status}
                      </span>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <button style={{
                        padding: '0.25rem 0.75rem',
                        backgroundColor: theme === 'dark' ? 'rgba(102, 126, 234, 0.2)' : '#f3f4f6',
                        border: 'none',
                        borderRadius: '0.375rem',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        color: theme === 'dark' ? '#a8b2ff' : '#374151'
                      }}>
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default CreatorsLicenses;
